/**
 * Localizes AU size references inside the product Size & Fit accordion.
 * The underlying Shopify metafield stays unchanged; only customer-facing text changes.
 *
 * Aurelia Sun market rules:
 * AU: AU sizes
 * GB: UK sizes (same numeric apparel scale; footwear AU - 2)
 * US/NZ/SG: US sizes (apparel AU - 4; footwear same numeric scale)
 */

const MARKET_SIZE_FIT_SELECTOR = '[data-market-size-fit]';

function cleanNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return Number.isInteger(number) ? String(number) : String(number).replace(/\.0+$/, '');
}

function marketRule(country, isFootwear) {
  switch ((country || 'AU').toUpperCase()) {
    case 'GB':
      return { prefix: 'UK', offset: isFootwear ? -2 : 0 };
    case 'US':
    case 'NZ':
    case 'SG':
      return { prefix: 'US', offset: isFootwear ? 0 : -4 };
    default:
      return { prefix: 'AU', offset: 0 };
  }
}

function convertNumber(rawNumber, offset) {
  const number = Number(rawNumber);
  if (!Number.isFinite(number)) return rawNumber;
  return cleanNumber(number + offset);
}

function localizeSizeText(text, country, isFootwear) {
  const rule = marketRule(country, isFootwear);
  if (rule.prefix === 'AU' && rule.offset === 0) return text;

  let output = text;

  // Handles AU 10, AU10, AU 8.5, AU 6/8, AU 8–10, AU 8 - 10,
  // and ranges where the second value repeats AU, e.g. AU 8–AU 10.
  output = output.replace(
    /\bAU\s*(\d+(?:\.\d+)?)(?:\s*(\/|–|—|-|\bto\b)\s*(?:AU\s*)?(\d+(?:\.\d+)?))?/gi,
    (match, first, separator, second) => {
      const firstConverted = convertNumber(first, rule.offset);
      if (!second) return `${rule.prefix} ${firstConverted}`;

      const secondConverted = convertNumber(second, rule.offset);
      const joiner = separator.toLowerCase() === 'to' ? ' to ' : separator;
      return `${rule.prefix} ${firstConverted}${joiner}${secondConverted}`;
    }
  );

  // Covers wording such as "AU sizing" or "Australian sizing" when no
  // explicit number follows it.
  if (rule.prefix === 'UK') {
    output = output
      .replace(/\bAU sizing\b/gi, 'UK sizing')
      .replace(/\bAustralian sizing\b/gi, 'UK sizing');
  } else if (rule.prefix === 'US') {
    output = output
      .replace(/\bAU sizing\b/gi, 'US sizing')
      .replace(/\bAustralian sizing\b/gi, 'US sizing');
  }

  return output;
}

function localizeContainer(container) {
  if (!(container instanceof HTMLElement)) return;
  if (container.dataset.marketSizeFitLocalized === 'true') return;

  const country = container.dataset.marketCountry || 'AU';
  const isFootwear = container.dataset.marketFootwear === 'true';

  if (country.toUpperCase() === 'AU') {
    container.dataset.marketSizeFitLocalized = 'true';
    return;
  }

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest('script, style, noscript')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const nodes = [];
  let node;
  while ((node = walker.nextNode())) nodes.push(node);

  for (const textNode of nodes) {
    const original = textNode.nodeValue || '';
    const localized = localizeSizeText(original, country, isFootwear);
    if (localized !== original) textNode.nodeValue = localized;
  }

  container.dataset.marketSizeFitLocalized = 'true';
}

function initMarketSizeFit(root = document) {
  if (root instanceof HTMLElement && root.matches(MARKET_SIZE_FIT_SELECTOR)) {
    localizeContainer(root);
  }

  root.querySelectorAll?.(MARKET_SIZE_FIT_SELECTOR).forEach(localizeContainer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initMarketSizeFit(), { once: true });
} else {
  initMarketSizeFit();
}

document.addEventListener('shopify:section:load', (event) => {
  initMarketSizeFit(event.target);
});
